import {
  AppFrame,
  Bell,
  openSection,
  placeKey,
  rememberVisit,
  SearchButton,
  toggleRail,
  useGlobalShortcuts,
  useLiveRoom,
  ViewerProvider,
} from "@nexxt/app-kit";
import { CatalogTextsProvider, fieldLabel } from "@nexxt/catalog-ui";
import { internalQueuesRoom } from "@nexxt/contracts/realtime";
import { useMediaQuery, wideLayout } from "@nexxt/ui";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { refreshQueues } from "../api";
import { bellClient } from "../bell-client";
import { useIsInternal, useMe } from "../features/auth/me";
import { mappingLabel } from "../features/code-mapping/code-mapping-texts";
import { followClient } from "../follow-client";
import { CommandMenu } from "./command-menu";
import { areaOf, placeOn, useNavItems } from "./navigation";
import { ShortcutsDialog } from "./shortcuts-dialog";
import { AppSidebar } from "./sidebar";
import { UserMenu } from "./user-menu";

// Backstage in app-kit's frame: the sidebar (ADR-114), the ⌘K palette, the list of shortcuts. Dense and keyboard first
// (principi-design.md, point 5): from `lg` the sidebar is a column, wide or of icons; below, the top bar has the search
// and the user.

export function AppShell() {
  const places = useNavItems();
  const { pathname, href } = useRouterState({ select: (s) => s.location });
  const current = placeOn(places, pathname);
  const area = areaOf(current);
  // the way back from the admin area: the last page of the app, filters included
  const [backTo, setBackTo] = useState("/");
  if (area === "app" && backTo !== href) setBackTo(href);
  // a place that comes on display (also from ⌘K or a `g` shortcut) opens its section and counts as a visit
  useEffect(() => {
    if (!current?.section) return;
    openSection(current.section);
    rememberVisit({ key: placeKey(current.id), place: current.id, href: current.to });
  }, [current]);

  // the counts of the home, of the queues pinned in the sidebar and of ⌘K, once for every page (ADR-285)
  useLiveRoom(internalQueuesRoom, [], { read: refreshQueues });

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const wide = useMediaQuery(wideLayout);
  const { permissions } = useMe();
  const isInternal = useIsInternal();
  useGlobalShortcuts(
    {
      togglePalette: () => setPaletteOpen((open) => !open),
      toggleRail: wide ? toggleRail : undefined,
      showShortcuts: () => setShortcutsOpen(true),
    },
    places,
  );

  const sidebar = (closeMenu: () => void) => ({
    area,
    places,
    current,
    backTo,
    onSearch: () => {
      closeMenu();
      setPaletteOpen(true);
    },
    onShortcuts: () => {
      closeMenu();
      setShortcutsOpen(true);
    },
  });

  return (
    // the screens of the domains (ADR-166) with the user of Backstage; the catalog's with the words of the missing
    // codes of other systems
    <ViewerProvider value={{ isInternal, permissions }}>
      <CatalogTextsProvider value={{ fieldText: (field) => mappingLabel(field, fieldLabel) }}>
        <AppFrame
          product="backstage"
          sidebar={({ variant, closeMenu }) => (
            <AppSidebar
              variant={variant}
              {...sidebar(closeMenu)}
              {...(variant === "sheet" && { onFollowLink: closeMenu })}
            />
          )}
          topBar={({ closeMenu }) => {
            const { onSearch, onShortcuts } = sidebar(closeMenu);
            return (
              <>
                <SearchButton onClick={onSearch} compact className="ml-auto" />
                {!wide && <Bell client={bellClient} follows={followClient} variant="icon" />}
                <UserMenu compact onShortcuts={onShortcuts} />
              </>
            );
          }}
        >
          <CommandMenu
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            places={places}
            current={current}
            onShortcuts={() => setShortcutsOpen(true)}
          />
          <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} places={places} />
        </AppFrame>
      </CatalogTextsProvider>
    </ViewerProvider>
  );
}
